// src/pages/Dashboard.js — UI "Command Center" v4
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Paper, Stack, Typography, Chip, Divider,
  Table, TableHead, TableRow, TableCell, TableBody,
  Alert, Button, CircularProgress, LinearProgress,
  Avatar, Tooltip, IconButton,
} from "@mui/material";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import RefreshIcon        from "@mui/icons-material/Refresh";
import AccessTimeIcon     from "@mui/icons-material/AccessTime";
import CheckCircleIcon    from "@mui/icons-material/CheckCircle";
import ScheduleIcon       from "@mui/icons-material/Schedule";
import ErrorOutlineIcon   from "@mui/icons-material/ErrorOutline";
import PlayCircleIcon     from "@mui/icons-material/PlayCircle";
import OpenInNewIcon      from "@mui/icons-material/OpenInNew";
import CalendarMonthIcon  from "@mui/icons-material/CalendarMonth";
import BoltIcon           from "@mui/icons-material/Bolt";
import BarChartIcon       from "@mui/icons-material/BarChart";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ArrowForwardIcon   from "@mui/icons-material/ArrowForward";
import { useNavigate }    from "react-router-dom";
import { useTranslation } from "react-i18next";
import API, { getUserMessage } from "../api/axios";
import { fmtLocal }       from "../utils/datetime";
import { useAuth }        from "../auth/useAuth";

// ── Palette ──────────────────────────────────────────────────────────────────
const C = {
  teal:     "#0f766e",
  tealBg:   "#f0fdfa",
  tealMid:  "#ccfbf1",
  amber:    "#b45309",
  amberBg:  "#fffbeb",
  red:      "#dc2626",
  redBg:    "#fef2f2",
  slate:    "#475569",
  slateBg:  "#f8fafc",
  indigo:   "#4f46e5",
  indigoBg: "#eef2ff",
  violet:   "#7c3aed",
  violetBg: "#f5f3ff",
};

const STATUS_META = {
  running:   { color: C.teal,   bg: C.tealBg,   label: "En cours",   dot: true  },
  scheduled: { color: C.indigo, bg: C.indigoBg,  label: "Planifiée",  dot: false },
  approved:  { color: C.indigo, bg: C.indigoBg,  label: "Approuvée",  dot: false },
  completed: { color: C.slate,  bg: C.slateBg,   label: "Terminée",   dot: false },
  cancelled: { color: C.amber,  bg: C.amberBg,   label: "Annulée",    dot: false },
  failed:    { color: C.red,    bg: C.redBg,     label: "Échouée",    dot: false },
};
const normStatus = s => String(s || "scheduled").toLowerCase().trim();

// ── Hook timer live ──────────────────────────────────────────────────────────
function useNow() {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

function fmtCountdown(ms) {
  if (ms <= 0) return "00:00";
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  if (h > 0) return `${h}h ${String(m % 60).padStart(2, "0")}m`;
  return `${String(m).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

// ── Badge statut ─────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const s = normStatus(status);
  const m = STATUS_META[s] || STATUS_META.scheduled;
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{
      display: "inline-flex", px: 1.25, py: 0.35, borderRadius: 1.5,
      bgcolor: m.bg, width: "fit-content",
    }}>
      {m.dot && (
        <Box sx={{
          width: 6, height: 6, borderRadius: "50%", bgcolor: m.color,
          animation: "dotPulse 1.5s ease-in-out infinite",
          "@keyframes dotPulse": { "0%,100%": { opacity: 1 }, "50%": { opacity: .3 } },
        }} />
      )}
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: m.color, letterSpacing: .3 }}>
        {m.label}
      </Typography>
    </Stack>
  );
}

// ── Bannière lab actif ───────────────────────────────────────────────────────
function ActiveBanner({ reservation, now }) {
  const navigate  = useNavigate();
  const end       = new Date(reservation.end_time).getTime();
  const start     = new Date(reservation.start_time).getTime();
  const remaining = end - now;
  const progress  = Math.min(100, Math.max(0, ((now - start) / (end - start)) * 100));
  const urgent    = remaining < 15 * 60 * 1000;
  const color     = urgent ? C.red : C.teal;
  const colorBg   = urgent ? "#fef2f2" : "#f0fdfa";
  const colorBrd  = urgent ? "#fca5a5" : "#99f6e4";

  return (
    <Box sx={{
      borderRadius: 3, p: 3, mb: 3,
      background: urgent
        ? "linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)"
        : "linear-gradient(135deg, #f0fdfa 0%, #e0f7f4 100%)",
      border: `1.5px solid ${colorBrd}`,
      position: "relative", overflow: "hidden",
    }}>
      <Box sx={{ position: "absolute", right: -28, top: -28,
        width: 130, height: 130, borderRadius: "50%",
        bgcolor: urgent ? "#fca5a520" : "#99f6e420" }} />
      <Box sx={{ position: "absolute", right: 60, bottom: -40,
        width: 80, height: 80, borderRadius: "50%",
        bgcolor: urgent ? "#fca5a510" : "#99f6e410" }} />

      <Stack direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between" alignItems={{ sm: "center" }} spacing={2}>
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ width: 32, height: 32, borderRadius: 2,
              bgcolor: urgent ? C.redBg : C.tealBg,
              display: "flex", alignItems: "center", justifyContent: "center" }}>
              <BoltIcon sx={{ fontSize: 16, color }} />
            </Box>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography fontWeight={800} sx={{ fontSize: 15, color }}>
                  {reservation.lab_name || `Lab #${reservation.id}`}
                </Typography>
                <Chip size="small" label="EN COURS"
                  sx={{ height: 16, fontSize: 9, fontWeight: 800, letterSpacing: .5,
                    bgcolor: color, color: "#fff" }} />
              </Stack>
            </Box>
          </Stack>

          <Stack direction="row" spacing={3} alignItems="center">
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontSize: 10 }}>
                Temps restant
              </Typography>
              <Typography fontWeight={800} sx={{
                fontSize: 28, color, lineHeight: 1.1,
                fontVariantNumeric: "tabular-nums", letterSpacing: -1,
              }}>
                {fmtCountdown(remaining)}
              </Typography>
            </Box>
            <Box sx={{ width: 180 }}>
              <Typography variant="caption" color="text.secondary"
                sx={{ display: "block", fontSize: 10, mb: 0.5 }}>
                Progression {Math.round(progress)}%
              </Typography>
              <LinearProgress variant="determinate" value={progress}
                sx={{ height: 6, borderRadius: 3,
                  bgcolor: urgent ? "#fecaca" : "#b2f5ea",
                  "& .MuiLinearProgress-bar": {
                    bgcolor: color, borderRadius: 3,
                    transition: "width 1s linear",
                  },
                }} />
            </Box>
          </Stack>
        </Stack>

        <Button variant="contained" endIcon={<OpenInNewIcon />}
          onClick={() => navigate(`/lab/${reservation.id}`)}
          sx={{
            bgcolor: color, "&:hover": { bgcolor: urgent ? "#b91c1c" : "#0d9488" },
            borderRadius: 2.5, px: 3, py: 1.25, fontWeight: 700,
            fontSize: 13, letterSpacing: .3, boxShadow: "none",
            whiteSpace: "nowrap",
          }}>
          Rejoindre le lab
        </Button>
      </Stack>
    </Box>
  );
}

// ── Sidebar crédits ──────────────────────────────────────────────────────────
function CreditSidebar({ credits, loading }) {
  const navigate = useNavigate();
  const now      = new Date();

  if (loading) return (
    <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider",
      borderRadius: 3, p: 3 }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <CircularProgress size={16} />
        <Typography variant="caption" color="text.secondary">Chargement crédits…</Typography>
      </Stack>
    </Paper>
  );
  if (!credits) return null;

  const remaining = credits.remaining_minutes ?? 0;
  const total     = credits.total_minutes ?? 0;
  const pct       = total > 0 ? Math.round((remaining / total) * 100) : 0;
  const isLow     = pct < 20 && total > 0;
  const wallets   = (credits.wallets || []).filter(w => {
    const ea = w.expires_at ? new Date(w.expires_at) : null;
    return w.is_active !== false && (!ea || ea > now) && (w.remaining_minutes ?? 0) > 0;
  });

  return (
    <Paper elevation={0} sx={{
      border: "1.5px solid", borderColor: isLow ? "#fca5a5" : "divider",
      bgcolor: isLow ? "#fff9f9" : "background.paper",
      borderRadius: 3, overflow: "hidden",
    }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between"
        sx={{ px: 2.5, py: 2, borderBottom: "1px solid", borderColor: "divider" }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <AccountBalanceWalletIcon sx={{ fontSize: 15, color: isLow ? C.red : C.teal }} />
          <Typography variant="subtitle2" fontWeight={700}>Crédits labs</Typography>
          {isLow && (
            <Chip size="small" label="⚠ Faible"
              sx={{ height: 16, fontSize: 9, fontWeight: 700,
                bgcolor: C.redBg, color: C.red }} />
          )}
        </Stack>
        <Button size="small" onClick={() => navigate("/user/credits")}
          endIcon={<ArrowForwardIcon sx={{ fontSize: 11 }} />}
          sx={{ fontSize: 11, color: C.teal, p: 0, minWidth: 0,
            "&:hover": { bgcolor: "transparent", textDecoration: "underline" } }}>
          Tout voir
        </Button>
      </Stack>

      <Box sx={{ p: 2.5 }}>
        {/* Solde */}
        <Box sx={{ mb: wallets.length > 0 ? 2 : 0 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.75 }}>
            <Stack direction="row" spacing={0.5} alignItems="baseline">
              <Typography variant="h3" fontWeight={800} lineHeight={1}
                sx={{ color: isLow ? C.red : C.teal, fontVariantNumeric: "tabular-nums" }}>
                {remaining}
              </Typography>
              <Typography variant="caption" color="text.secondary">min</Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary">/ {total} min</Typography>
          </Stack>
          <LinearProgress variant="determinate" value={pct}
            sx={{ height: 8, borderRadius: 4, bgcolor: "#f1f5f9",
              "& .MuiLinearProgress-bar": {
                bgcolor: isLow ? C.red : pct < 50 ? C.amber : C.teal,
                borderRadius: 4,
              },
            }} />
          <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.75 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
              {pct}% restant
            </Typography>
            <Typography variant="caption" sx={{ fontSize: 10,
              color: isLow ? C.red : "text.secondary",
              fontWeight: isLow ? 700 : 400 }}>
              {isLow ? "Rechargez vos crédits"
                : `${credits.active_wallets ?? 0} wallet${(credits.active_wallets ?? 0) > 1 ? "s" : ""} actif${(credits.active_wallets ?? 0) > 1 ? "s" : ""}`}
            </Typography>
          </Stack>
        </Box>

        {/* Wallets */}
        {wallets.length > 0 && (
          <Stack spacing={1.25}>
            <Divider />
            {wallets.slice(0, 3).map(w => {
              const wp   = w.total_minutes > 0
                ? Math.round((w.remaining_minutes / w.total_minutes) * 100) : 0;
              const wc   = wp > 50 ? C.teal : wp > 20 ? C.amber : C.red;
              const ea   = w.expires_at ? new Date(w.expires_at) : null;
              const days = ea ? Math.ceil((ea - now) / 86400000) : null;
              return (
                <Box key={w.id}>
                  <Stack direction="row" justifyContent="space-between"
                    alignItems="center" sx={{ mb: 0.5 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Typography variant="caption" fontWeight={700} noWrap sx={{ maxWidth: 100, fontSize: 12 }}>
                        {w.pack_code || `#${w.id}`}
                      </Typography>
                      {days !== null && days <= 7 && (
                        <Chip size="small" label={`${days}j`}
                          sx={{ height: 14, fontSize: 9, fontWeight: 700,
                            bgcolor: days <= 2 ? C.redBg : C.amberBg,
                            color:   days <= 2 ? C.red   : C.amber }} />
                      )}
                    </Stack>
                    <Typography variant="caption" fontWeight={700} sx={{ color: wc, fontSize: 12 }}>
                      {Math.round(w.remaining_minutes)} min
                    </Typography>
                  </Stack>
                  <LinearProgress variant="determinate" value={wp}
                    sx={{ height: 4, borderRadius: 2, bgcolor: "#f1f5f9",
                      "& .MuiLinearProgress-bar": { bgcolor: wc, borderRadius: 2 } }} />
                </Box>
              );
            })}
            {wallets.length > 3 && (
              <Typography variant="caption" color="text.secondary"
                sx={{ fontSize: 10, textAlign: "center" }}>
                + {wallets.length - 3} wallet{wallets.length - 3 > 1 ? "s" : ""} supplémentaire{wallets.length - 3 > 1 ? "s" : ""}
              </Typography>
            )}
          </Stack>
        )}
        {wallets.length === 0 && (
          <Box sx={{ textAlign: "center", py: 1 }}>
            <Typography variant="caption" color="text.secondary">Aucun crédit actif</Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
}

// ── Prochaines sessions ──────────────────────────────────────────────────────
function UpcomingSessions({ reservations, now }) {
  const navigate = useNavigate();
  const upcoming = reservations
    .filter(r => ["scheduled","approved"].includes(normStatus(r.status))
      && new Date(r.start_time) > new Date(now))
    .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
    .slice(0, 5);

  if (!upcoming.length) return null;

  return (
    <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider",
      borderRadius: 3, overflow: "hidden", mt: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1}
        sx={{ px: 2.5, py: 2, borderBottom: "1px solid", borderColor: "divider" }}>
        <CalendarMonthIcon sx={{ fontSize: 15, color: C.indigo }} />
        <Typography variant="subtitle2" fontWeight={700}>Prochaines sessions</Typography>
        <Chip size="small" label={upcoming.length}
          sx={{ height: 16, fontSize: 9, fontWeight: 700, ml: "auto !important",
            bgcolor: C.indigoBg, color: C.indigo }} />
      </Stack>
      <Stack sx={{ px: 2, py: 0.5 }}>
        {upcoming.map((r, i) => {
          const start   = new Date(r.start_time);
          const isToday = start.toDateString() === new Date().toDateString();
          const left    = start.getTime() - now;
          const soon    = left < 24 * 3600 * 1000 && left > 0;
          return (
            <Stack key={r.id} direction="row" alignItems="center"
              justifyContent="space-between" sx={{
                py: 1.25,
                borderBottom: i < upcoming.length - 1 ? "1px solid #f1f5f9" : "none",
              }}>
              <Stack spacing={0.25}>
                <Typography variant="caption" fontWeight={700} sx={{ fontSize: 12 }}>
                  {r.lab_name || `Lab #${r.lab_id}`}
                </Typography>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  {isToday && (
                    <Chip size="small" label="Auj."
                      sx={{ height: 14, fontSize: 9, fontWeight: 700,
                        bgcolor: C.tealBg, color: C.teal }} />
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                    {start.toLocaleDateString("fr", { weekday:"short", day:"numeric", month:"short" })}
                    {" · "}{start.toLocaleTimeString("fr", { hour:"2-digit", minute:"2-digit" })}
                  </Typography>
                </Stack>
              </Stack>
              {soon && (
                <Typography variant="caption" fontWeight={700}
                  sx={{ color: C.amber, fontSize: 11, fontVariantNumeric: "tabular-nums" }}>
                  dans {fmtCountdown(left)}
                </Typography>
              )}
            </Stack>
          );
        })}
      </Stack>
    </Paper>
  );
}

// ── Actions rapides ──────────────────────────────────────────────────────────
function QuickActions({ isAdmin }) {
  const navigate = useNavigate();
  const actions = [
    { label: "Nouveau lab",       icon: <AddCircleOutlineIcon sx={{ fontSize: 16 }} />, path: "/labs",          color: C.teal,   bg: C.tealBg   },
    { label: "Mes réservations",  icon: <CalendarMonthIcon   sx={{ fontSize: 16 }} />, path: "/reservations",  color: C.indigo, bg: C.indigoBg },
    { label: "Statistiques",      icon: <BarChartIcon        sx={{ fontSize: 16 }} />, path: "/me/stats",      color: C.violet, bg: C.violetBg },
    { label: "Mes crédits",       icon: <AccountBalanceWalletIcon sx={{ fontSize: 16 }} />, path: "/user/credits", color: C.amber, bg: C.amberBg },
    ...(isAdmin ? [
      { label: "Administration",  icon: <BoltIcon sx={{ fontSize: 16 }} />, path: "/admin",  color: C.slate, bg: C.slateBg },
    ] : []),
  ];

  return (
    <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider",
      borderRadius: 3, overflow: "hidden", mt: 2 }}>
      <Box sx={{ px: 2.5, py: 2, borderBottom: "1px solid", borderColor: "divider" }}>
        <Typography variant="subtitle2" fontWeight={700}>Actions rapides</Typography>
      </Box>
      <Stack spacing={0.75} sx={{ p: 1.5 }}>
        {actions.map(({ label, icon, path, color, bg }) => (
          <Button key={path} fullWidth variant="text" size="small"
            startIcon={icon} onClick={() => navigate(path)}
            sx={{
              borderRadius: 2, justifyContent: "flex-start",
              fontSize: 12, fontWeight: 600, color, py: 0.75, px: 1.5,
              "&:hover": { bgcolor: bg },
            }}>
            {label}
          </Button>
        ))}
      </Stack>
    </Paper>
  );
}

// ── Page principale ──────────────────────────────────────────────────────────
export default function Dashboard() {
  const { t }    = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const now      = useNow();
  const isAdmin  = user?.role === "admin";
  const mountRef = useRef(true);

  const [reservations, setReservations] = useState([]);
  const [total,        setTotal]        = useState(0);
  const [error,        setError]        = useState("");
  const [loading,      setLoading]      = useState(false);
  const [credits,      setCredits]      = useState(null);
  const [credLoading,  setCredLoading]  = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!mountRef.current) return;
    try {
      setLoading(true);
      if (!silent) setError("");
      const { data } = await API.get("/reservations?limit=200&offset=0");
      const items = Array.isArray(data) ? data : (data?.items ?? []);
      const tot   = typeof data?.total === "number" ? data.total : items.length;
      if (mountRef.current) { setReservations(items); setTotal(tot); }
    } catch (e) {
      if (!mountRef.current) return;
      if (!silent) setError(getUserMessage(e, "Impossible de charger les réservations."));
    } finally {
      if (mountRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mountRef.current = true;
    load(true);
    setCredLoading(true);
    API.get("/reservations/lab-credits/me")
      .then(({ data }) => { if (mountRef.current) setCredits(data?.summary || null); })
      .catch(() => {})
      .finally(() => { if (mountRef.current) setCredLoading(false); });
    return () => { mountRef.current = false; };
  }, [load]);

  const counts = useMemo(() => {
    const acc = { scheduled: 0, approved: 0, running: 0, completed: 0, cancelled: 0, failed: 0 };
    reservations.forEach(r => {
      const s = normStatus(r?.status);
      if (s in acc) acc[s]++;
    });
    return acc;
  }, [reservations]);

  const activeRes = useMemo(() =>
    reservations.find(r => normStatus(r.status) === "running" && new Date(r.end_time) > new Date()),
    [reservations]
  );

  const recent = useMemo(() =>
    [...reservations]
      .sort((a, b) => new Date(b.start_time) - new Date(a.start_time))
      .slice(0, 8),
    [reservations]
  );

  // Chercher le nom dans tous les champs possibles retournés par useAuth
  const displayName = (
    user?.full_name ||
    user?.name      ||
    user?.username  ||
    user?.sub       ||
    user?.email?.split("@")[0] ||
    "vous"
  );
  const firstName   = displayName.split(" ")[0];
  const initials    = displayName.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  const hour        = new Date().getHours();
  const greeting    = hour < 12 ? "Bonjour" : hour < 18 ? "Bon après-midi" : "Bonsoir";
  const dateStr     = new Date().toLocaleDateString("fr-FR",
    { weekday: "long", day: "numeric", month: "long" });

  const plannedCount = counts.scheduled + counts.approved;

  return (
    <Box sx={{ maxWidth: 1280, mx: "auto", my: { xs: 2, md: 4 }, px: { xs: 2, md: 3 } }}>

      {/* ── Header ──────────────────────────────────────────────────── */}
      <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ sm: "center" }}
        justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar sx={{
            width: 48, height: 48, borderRadius: 2.5,
            bgcolor: C.teal, fontSize: 16, fontWeight: 800,
            boxShadow: "0 0 0 3px #f0fdfa, 0 0 0 4px #ccfbf1",
          }}>
            {initials}
          </Avatar>
          <Box>
            <Stack direction="row" spacing={1} alignItems="baseline">
              <Typography fontWeight={900} sx={{ fontSize: { xs: 22, md: 26 }, letterSpacing: -0.5 }}>
                {greeting},
              </Typography>
              <Typography fontWeight={900}
                sx={{ fontSize: { xs: 22, md: 26 }, letterSpacing: -0.5, color: C.teal }}>
                {firstName}
              </Typography>
              {isAdmin && (
                <Chip size="small" label="Admin"
                  sx={{ height: 18, fontSize: 10, fontWeight: 700,
                    bgcolor: C.indigoBg, color: C.indigo }} />
              )}
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
              {dateStr} · {total} réservation{total > 1 ? "s" : ""}
              {activeRes && (
                <Box component="span" sx={{ ml: 1.5, color: C.teal, fontWeight: 700 }}>
                  · 1 lab actif
                </Box>
              )}
            </Typography>
          </Box>
        </Stack>

        <Tooltip title="Rafraîchir">
          <IconButton onClick={() => load(false)} disabled={loading} size="small"
            sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
            {loading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Stack>

      {error && (
        <Alert severity="error" onClose={() => setError("")}
          sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>
      )}

      {/* ── Bannière lab actif ───────────────────────────────────────── */}
      {activeRes && <ActiveBanner reservation={activeRes} now={now} />}

      {/* ── Layout 2 colonnes ────────────────────────────────────────── */}
      <Stack direction={{ xs: "column", lg: "row" }} spacing={3} alignItems="flex-start">

        {/* ── Colonne principale ─────────────────────────────────────── */}
        <Box sx={{ flex: 1, minWidth: 0 }}>

          {/* KPIs */}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}
            flexWrap="wrap" sx={{ mb: 3 }}>
            {[
              {
                label: "Planifiées",
                value: plannedCount,
                color: C.indigo, bg: C.indigoBg,
                icon: <ScheduleIcon sx={{ fontSize: 18 }} />,
                sub: plannedCount > 0 ? `${plannedCount} à venir` : null,
              },
              {
                label: "En cours",
                value: counts.running,
                color: C.teal, bg: C.tealBg,
                icon: <PlayCircleIcon sx={{ fontSize: 18 }} />,
                pulse: counts.running > 0,
                sub: counts.running > 0 ? "Lab actif !" : null,
              },
              {
                label: "Terminées",
                value: counts.completed,
                color: C.slate, bg: C.slateBg,
                icon: <CheckCircleIcon sx={{ fontSize: 18 }} />,
              },
              {
                label: "Annulées / Échouées",
                value: counts.cancelled + counts.failed,
                color: C.amber, bg: C.amberBg,
                icon: <ErrorOutlineIcon sx={{ fontSize: 18 }} />,
                sub: counts.failed > 0 ? `dont ${counts.failed} échouée${counts.failed > 1 ? "s" : ""}` : null,
              },
            ].map(({ label, value, color, bg, icon, pulse, sub }) => (
              <Paper key={label} elevation={0} sx={{
                flex: 1, minWidth: 130, p: 2, borderRadius: 2.5,
                border: "1px solid", borderColor: value > 0 && pulse ? color + "40" : "divider",
                position: "relative", overflow: "hidden",
                transition: "box-shadow .2s, transform .15s",
                "&:hover": { boxShadow: "0 4px 16px rgba(0,0,0,.07)", transform: "translateY(-1px)" },
              }}>
                {pulse && value > 0 && (
                  <Box sx={{
                    position: "absolute", top: 8, right: 8,
                    width: 7, height: 7, borderRadius: "50%", bgcolor: color,
                    animation: "kpiPulse 1.5s ease-in-out infinite",
                    "@keyframes kpiPulse": {
                      "0%,100%": { opacity: 1, transform: "scale(1)" },
                      "50%": { opacity: .4, transform: "scale(1.6)" },
                    },
                  }} />
                )}
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: bg,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color, flexShrink: 0 }}>
                    {icon}
                  </Box>
                  <Box>
                    <Typography variant="h5" fontWeight={800} lineHeight={1}
                      sx={{ fontVariantNumeric: "tabular-nums" }}>
                      {value}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                      {label}
                    </Typography>
                    {sub && (
                      <Typography variant="caption" sx={{ color, display: "block", fontSize: 10 }}>
                        {sub}
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </Paper>
            ))}
          </Stack>

          {/* Tableau activité récente */}
          <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider",
            borderRadius: 3, overflow: "hidden" }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between"
              sx={{ px: 3, py: 2, borderBottom: "1px solid", borderColor: "divider" }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <AccessTimeIcon sx={{ fontSize: 15, color: "text.secondary" }} />
                <Typography variant="subtitle2" fontWeight={700}>Activité récente</Typography>
                {recent.length > 0 && (
                  <Chip size="small" label={recent.length}
                    sx={{ height: 16, fontSize: 9, fontWeight: 700,
                      bgcolor: "#f8fafc", color: "text.secondary" }} />
                )}
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                {loading && <CircularProgress size={13} />}
                <Button size="small" variant="text" endIcon={<ArrowForwardIcon sx={{ fontSize: 12 }} />}
                  onClick={() => navigate("/reservations")}
                  sx={{ borderRadius: 2, fontSize: 11, color: C.teal,
                    "&:hover": { bgcolor: C.tealBg } }}>
                  Tout voir
                </Button>
              </Stack>
            </Stack>

            {recent.length === 0 && !loading ? (
              <Box sx={{ py: 7, textAlign: "center" }}>
                <CalendarMonthIcon sx={{ fontSize: 36, color: "text.disabled", mb: 1.5 }} />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Aucune réservation pour l'instant
                </Typography>
                <Button variant="contained" size="small"
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={() => navigate("/labs")}
                  sx={{ borderRadius: 2, bgcolor: C.teal,
                    "&:hover": { bgcolor: "#0d9488" }, boxShadow: "none", fontSize: 12 }}>
                  Découvrir les labs
                </Button>
              </Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "#fafafa" }}>
                    {["#", ...(isAdmin ? ["Utilisateur"] : []),
                      "Lab", "Début", "Fin", "Statut", ""].map(h => (
                      <TableCell key={h} sx={{
                        fontWeight: 700, fontSize: 10, color: "text.disabled",
                        textTransform: "uppercase", letterSpacing: .5,
                        borderBottom: "1px solid #f1f5f9", py: 1.5,
                      }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recent.map(r => {
                    const s = normStatus(r.status);
                    return (
                      <TableRow key={r.id} sx={{
                        cursor: s === "running" ? "pointer" : "default",
                        "&:hover": { bgcolor: "#fafafa" },
                        "& td": { borderBottom: "1px solid #f8fafc" },
                        "&:last-child td": { borderBottom: 0 },
                      }}>
                        <TableCell sx={{ color: "text.disabled", fontFamily: "monospace",
                          fontSize: 11, py: 1.5 }}>
                          #{r.id}
                        </TableCell>
                        {isAdmin && (
                          <TableCell sx={{ py: 1.5 }}>
                            <Stack direction="row" spacing={0.75} alignItems="center">
                              <Avatar sx={{ width: 20, height: 20, fontSize: 9, bgcolor: C.indigo }}>
                                {String(r.user_name || r.user_id || "?")[0].toUpperCase()}
                              </Avatar>
                              <Typography variant="caption" noWrap sx={{ maxWidth: 100 }}>
                                {r.user_name || r.user_id || "—"}
                              </Typography>
                            </Stack>
                          </TableCell>
                        )}
                        <TableCell sx={{ py: 1.5 }}>
                          <Typography variant="body2" fontWeight={600} sx={{ fontSize: 13 }}>
                            {r.lab_name || `Lab #${r.lab_id}`}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, color: "text.secondary",
                          whiteSpace: "nowrap", py: 1.5 }}>
                          {fmtLocal(r.start_time)}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, color: "text.secondary",
                          whiteSpace: "nowrap", py: 1.5 }}>
                          {fmtLocal(r.end_time)}
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          <StatusBadge status={r.status} />
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          {s === "running" && (
                            <Tooltip title="Accéder au workspace">
                              <IconButton size="small"
                                onClick={() => navigate(`/lab/${r.id}`)}
                                sx={{ color: C.teal, p: 0.5,
                                  "&:hover": { bgcolor: C.tealBg } }}>
                                <OpenInNewIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Paper>
        </Box>

        {/* ── Sidebar droite ─────────────────────────────────────────── */}
        <Box sx={{ width: { xs: "100%", lg: 270 }, flexShrink: 0 }}>
          <CreditSidebar credits={credits} loading={credLoading} />
          <UpcomingSessions reservations={reservations} now={now} />
          <QuickActions isAdmin={isAdmin} />
        </Box>
      </Stack>
    </Box>
  );
}
